/**
 * The join between the saved roster and the campaign layer.
 *
 * A career sortie is never serialised into a URL: `/fly?career=<pilot id>`
 * carries the pilot, and `nextAssignment` regenerates the identical mission
 * from his saved state — the same trick `mission-launch.ts` plays with a
 * catalog id and a seed, one level up. A reload mid-briefing therefore lands on
 * the same sortie, and a reload *after* the debriefing lands on the next one,
 * because applying the debriefing is what moves the career on.
 *
 * That also makes double-crediting impossible: the flight route applies the
 * debriefing exactly once, at mission end, and the saved career it writes is
 * already past that mission.
 */

import { applyDebrief, nextAssignment, type CareerAssignment, type MissionDebrief } from "@red-opus/engine";
import { browserSettingsStore } from "../audio/settings.ts";
import type { SearchLike } from "../mission/mission-launch.ts";
import { applyRealism, currentRealism, type RealismPanel } from "../mission/realism-settings.ts";
import { findCareer, saveCareer, type CareerSave, type CareerStore } from "./career-store.ts";

export const CAREER_PARAM = "career";

/** The `/fly` URL that flies this pilot's next sortie. */
export function careerFlightPath(id: string): string {
  return `/fly?${CAREER_PARAM}=${encodeURIComponent(id)}`;
}

export function parseCareerLaunch(search: SearchLike): string | undefined {
  const value = search[CAREER_PARAM];
  const id = Array.isArray(value) ? value[0] : value;
  return id === undefined || id === "" ? undefined : id;
}

export interface CareerSortie {
  readonly save: CareerSave;
  readonly assignment: CareerAssignment;
}

/**
 * The pilot and the sortie he is due, or nothing if he cannot fly one. The
 * Realism Panel is stamped on here for the same reason `missionFor` does it:
 * `/fly` receives a spec that already carries the player's settings, so the
 * career route and the flight route both stay unaware the panel exists.
 */
export function careerSortie(store: CareerStore, id: string, panel?: RealismPanel): CareerSortie | undefined {
  const save = findCareer(store, id);
  if (!save) return undefined;
  const chosen = panel ?? currentRealism(browserSettingsStore());
  const assignment = nextAssignment(save.career, { aerodromeStart: chosen.aerodromeStart });
  if (!assignment) return undefined;
  return { save, assignment: { ...assignment, spec: applyRealism(assignment.spec, chosen) } };
}

/** Clears the consequences once the player has read them, so the hub comes back. */
export function acknowledgeConsequence(store: CareerStore, id: string): void {
  const save = findCareer(store, id);
  if (!save) return;
  saveCareer(store, save.career, { consequence: undefined });
}

/**
 * Lands the sortie: credits it, writes the new career and keeps the
 * consequences for the screen that shows them.
 */
export function landSortie(store: CareerStore, id: string, debrief: MissionDebrief): CareerSave | undefined {
  const save = findCareer(store, id);
  if (!save || save.career.status !== "active") return undefined;
  const update = applyDebrief(save.career, debrief);
  saveCareer(store, update.career, { consequence: update.consequence, debrief });
  return {
    career: update.career,
    lastConsequence: update.consequence,
    lastDebrief: debrief,
    updatedAt: new Date().toISOString(),
  };
}
